import React, { ReactElement, ReactNode, useState } from 'react';
import { CartItem, cartState } from 'app-state/cart-state';
import useCartActions from 'app-state/cart-actions';
import { TextField } from './TextField';
import formatPrice from 'utils/format-price';
import { useRecoilValue } from 'recoil';
import produce from 'immer';
import { ProductId } from 'utils/Product';
import CartQuantityModifer from './CartQuantityModifer';
import { IconButton } from './Icon';
import generateLocator from 'utils/generate-locator';
import { Voucher, VoucherUtils } from 'utils/Voucher';
import { xCircleIcon } from 'assets/icons';

interface CartTableProps {
  onRemoveItem?: (item: CartItem) => void,
  quantityModifierStyle?: 'input' | 'buttons'
  readonly?: boolean,
  columns?: string[],
  showTotal?: boolean,
  appliedVouchers?: Voucher[],
  borderBottom?: boolean,
}

export const CartTable = (props: CartTableProps): ReactElement => {
  const { cartContents, cartTotal } = useRecoilValue(cartState);
  const { setCartQuantity } = useCartActions();
  const getCartQuantites = () => {
    const obj: Record<ProductId, number> = {};
    for (const productId in cartContents) obj[productId] = cartContents[productId].cartQuantity;
    return obj;
  };
  const [tableQuantities, setTableQuantites] = useState(getCartQuantites());

  // This controls the ordering of the columns, and enforces that they are consistent between the table header and
  // the contents of the columns. To reorder the columns, simply reorder the elements in this object.
  const cartItemProperties: Record<string, (item: CartItem) => ReactElement> = {
    Quantity: (item: CartItem) => (
      <div className='grid place-content-center'>
        {!props.readonly && props.quantityModifierStyle === 'input' && <TextField
          data-locator='item-quantity'
          type="number"
          value={tableQuantities[item.product.id].toString()}
          onBlur={() => setCartQuantity({
            id: item.product.id, newQuantity: tableQuantities[item.product.id],
          })}
          onChange={event => {
            const newValue = event.target.value != '' ? Number.parseInt(event.target.value) : 0;
            setTableQuantites(produce(tableQuantities, draft => {
              draft[item.product.id] = Math.max(0, Math.min(newValue, item.product.quantity));
            }));
          }}
          className='w-14 text-center'
          restrictInput={(value) => value >= 0 && value <= item.product.quantity}
          validateInput={(value) => (value !== 0 && value !== '' && value !== '0')}
        />}
        {!props.readonly && props.quantityModifierStyle === 'buttons'
          && <span className='h-auto'><CartQuantityModifer product={item.product}/></span>}
        {(props.readonly || !props.quantityModifierStyle) && <span>{item.cartQuantity}</span>}
      </div>
    ),
    Item: (item: CartItem) => (
      <div className='flex flex-row justify-left'>
        <span className='hidden md:block md:flex-shrink-0' >
          <img
            src={item.product.image}
            alt={item.product.title}
            title={item.product.title}
            className='h-12 w-12 rounded-md border border-gray-400' />
        </span>
        <div className='flex ml-4 inline-block m-auto align-middle'>{item.product.title}</div>
      </div>
    ),
    Price: (item: CartItem) => (
      <span
        data-locator='price'
        className='flex justify-end'>{formatPrice(item.product.price)}</span>
    ),
    Subtotal: (item: CartItem) => (
      <span
        data-locator='subtotal'
        className='flex justify-end'>{formatPrice((item.cartQuantity * item.product.price))}</span>
    ),
    Actions: (item: CartItem) => (
      <IconButton
        data-locator='remove-from-cart-button'
        label={`Remove all ${item.product.title} from cart`}
        title={`Remove all ${item.product.title} from cart`}
        onClick={() => props.onRemoveItem && props.onRemoveItem(item)}
        src={xCircleIcon}
        alt='delete' />
    ),
  };
  let columns: string[] = props.columns || Object.keys(cartItemProperties);
  if (props.readonly) {
    columns = columns.filter((value) => value !== 'Actions');
  }

  // Generate a row with name and value within the footer, e.g. 'Total:' '$36.00' will be aligned with the
  // pricing columns in the table.
  const FooterRow = ({
    name, value,
  }: {
    name: string, value: string | ReactNode,
  }) => {
    // TODO: error handling here
    const subtotalPosition = columns.indexOf('Subtotal');
    const labelId = generateLocator('footer', name, 'label');
    return <tr>
      <td
        scope="col"
        colSpan={subtotalPosition}
        className='px-2 py-1 md:px-6 md:py-3 text-lg xl:text-xl'
      >
        <span id={labelId} className='flex justify-end'>{name}</span>
      </td>
      <td
        scope="col"
        className='px-2 py-1 md:px-6 md:py-3 text-lg lg:text-xl'
      >
        <span
          id={generateLocator('footer', name)}
          aria-labelledby={labelId}
          className='flex justify-end md:min-w-24'>{value}</span>
      </td>
    </tr>;
  };

  return <React.Fragment>
    <table id='cart-items' className='divide-y divide-gray-200 w-full'>
      <thead className='bg-gray-50'>
        <tr id='cart-table-headings'>
          {columns.map((col) =>
            <th
              scope="col"
              className='px-2 py-1 md:px-6 md:py-3 text-xs md:text-s text-center text-gray-500 uppercase tracking-wider'
              key={col}
            >
              <span className={`${col === 'Actions' && 'hidden md:block'}`}>{col}</span>
            </th>)}
        </tr>
      </thead>
      <tbody>
        {Object.values(cartContents).map((cartItem) =>
          <tr key={cartItem.product.id}>
            {columns.map(col =>
              <td key={`${cartItem.product.id}-${col}`} className='px-2 py-1 md:px-6 md:py-3 text-left lg:text-lg'>
                {cartItemProperties[col](cartItem)}
              </td>)}
          </tr>)}
      </tbody>
      {props.showTotal && <tfoot>
        {<FooterRow name='Total' value={(props.appliedVouchers && props.appliedVouchers.length > 0)
          ? formatPrice(cartTotal)
          : <strong>{formatPrice(cartTotal)}</strong>} />}
        {props.appliedVouchers && props.appliedVouchers.length > 0
          && <>
            {props.appliedVouchers.map((voucher => <FooterRow
              key={voucher.code}
              name={`${voucher.code} (-${VoucherUtils.formatVoucherDiscount(voucher)})`}
              value={`-${formatPrice(VoucherUtils.calculateVoucherDiscount(voucher, cartTotal))}`} />
            ))}
            <FooterRow name='You Pay:' value={<strong>{
              formatPrice(VoucherUtils.applyVouchersToTotal(props.appliedVouchers, cartTotal))
            }</strong>} />
          </>
        }
      </tfoot>}
    </table>
    {props.borderBottom 
      && <div className='w-full pb-1'><hr className='w-full border-b-1 border-gray-400' /></div>
    }
  </React.Fragment>;
};

export default CartTable;
